<template>
  <div class="container-fluid full-width-page-wrapper">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="font-weight-bold page-title mb-0">Edit Technical Areas Structure</h3>
      <div v-if="revision">
        <a
          class="btn btn-outline-primary mr-2"
          :href="pdfLink"
          target="_blank"
          rel="noopener"
        >
          Print PDF
        </a>
        <b-button
          v-if="revision.isEditable || revision.isValidated"
          class="mr-2"
          variant="outline-danger"
          :disabled="busy"
          @click="discardRevision"
        >
          Discard
        </b-button>
        <b-button
          v-if="revision.isEditable"
          class="mr-2"
          variant="primary"
          :disabled="busy"
          @click="saveSnapshot"
        >
          Save
        </b-button>
        <b-button
          v-if="revision.isEditable"
          variant="success"
          :disabled="busy"
          @click="requestValidation"
        >
          Ask for validation
        </b-button>
        <b-button
          v-if="revision.isValidated"
          variant="success"
          :disabled="busy"
          @click="applyRevision"
        >
          Apply changes
        </b-button>
      </div>
    </div>

    <b-alert v-if="forbidden" show variant="warning">
      You do not have permission to edit the technical areas structure.
    </b-alert>

    <ifac-loader v-else-if="busy && !revision && !canStartReview" />

    <div v-else-if="!revision && canStartReview">
      <p class="text-muted mb-3">
        Start a structure review. Choose whether changes apply to the current or next triennium.
      </p>
      <target-triennium-field v-model="targetTriennium" class="mb-3" />
      <b-button variant="primary" :disabled="busy" @click="startStructureReview">
        Start structure review
      </b-button>
    </div>

    <div v-else-if="!revision">
      <b-alert show variant="info">
        No structure review is currently in progress.
      </b-alert>
    </div>

    <div v-else>
      <target-triennium-field
        :target-triennium-number="revision.targetTrienniumNumber"
        readonly
      />

      <b-alert v-if="revision.status !== 'draft'" show variant="info">
        This revision is {{ revision.status.replace('_', ' ') }} and cannot be edited here.
        <router-link to="/areas/validate">Open validation view</router-link>
      </b-alert>

      <b-alert
        v-if="keywordWarnings.length"
        show
        variant="warning"
      >
        Removing these keywords will affect affiliate profiles:
        <ul class="mb-0 mt-2">
          <li v-for="warning in keywordWarnings" :key="warning.keywordId">
            {{ warning.name }} ({{ warning.affiliateCount }} {{ warning.affiliateCount === 1 ? 'affiliate' : 'affiliates' }})
          </li>
        </ul>
      </b-alert>

      <b-alert
        v-if="sponsorshipWarnings.length"
        show
        variant="warning"
      >
        Applying this structure will update conference Main/Co TC tags:
        <ul class="mb-0 mt-2">
          <li v-for="warning in sponsorshipWarnings" :key="warning.conferenceId">
            {{ sponsorshipWarningText(warning) }}
          </li>
        </ul>
      </b-alert>

      <div v-if="revision.isEditable">
        <b-button class="mb-3" size="sm" variant="outline-primary" @click="addCoordinatingCommittee">
          Add coordinating committee
        </b-button>

        <coordinating-committee-editor
          v-for="(committee, ccIndex) in activeCommittees"
          :key="committeeKey(committee, ccIndex)"
          :committee="committee"
          :can-move-technical-committees="canMoveTechnicalCommittees"
          :tc-move-options="tcMoveOptionsByCcKey[committeeKey(committee, ccIndex)] || []"
          :keyword-move-options-by-tc-key="keywordMoveOptionsByTcKey"
          :can-move-keywords="canMoveKeywords"
          :move-targets="moveTargets"
          :keyword-move-targets="keywordMoveTargets"
          :inherit-source-options="inheritSourceOptions"
          @remove="removeCoordinatingCommittee(ccIndex)"
          @add-technical-committee="addTechnicalCommittee(ccIndex)"
          @sync-numbers="syncTechnicalCommitteeNumbers(committee)"
          @remove-technical-committee="removeTechnicalCommittee(ccIndex, $event)"
          @set-move-target="setMoveTarget($event.tcKey, $event.target)"
          @move-technical-committee="moveTechnicalCommittee(ccIndex, $event.tcIndex, $event.target)"
          @sync-technical-committee-number="syncTechnicalCommitteeFullNumber(committee, $event)"
          @add-keyword="addKeyword(ccIndex, $event)"
          @remove-keyword="removeKeyword(ccIndex, $event.tcIndex, $event.keywordIndex)"
          @set-keyword-move-target="setKeywordMoveTarget($event.keywordKey, $event.target)"
          @move-keyword="moveKeyword(ccIndex, $event.tcIndex, $event.keywordIndex, $event.target)"
          @check-keyword-similarity="checkKeywordSimilarity"
        />
      </div>

      <ifac-technical-areas-accordion
        v-else
        :coordinating-committees="previewCommittees"
      />
    </div>
  </div>
</template>

<script>
import { IfacLoader, IfacTechnicalAreasAccordion } from '@ifac/ui';
import Triennium from '@/helpers/triennium';
import Revisions from '@/services/Api/TechnicalAreas/Revisions';
import CoordinatingCommitteeEditor from './partials/CoordinatingCommitteeEditor.vue';
import TargetTrienniumField from './TargetTrienniumField.vue';

function draftId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default {
  name: 'TechnicalAreasEdit',
  components: {
    CoordinatingCommitteeEditor,
    IfacLoader,
    IfacTechnicalAreasAccordion,
    TargetTrienniumField,
  },
  data() {
    return {
      busy: true,
      forbidden: false,
      revision: null,
      snapshot: { coordinatingCommittees: [] },
      keywordWarnings: [],
      sponsorshipWarnings: [],
      canStartReview: false,
      targetTriennium: 'current',
      moveTargets: {},
      keywordMoveTargets: {},
    };
  },
  computed: {
    activeCommittees() {
      return (this.snapshot.coordinatingCommittees || [])
        .filter((committee) => !committee.deleted);
    },
    canMoveTechnicalCommittees() {
      return this.activeCommittees.length > 1;
    },
    canMoveKeywords() {
      return this.activeCommittees
        .reduce((count, committee) => count + this.activeTechnicalCommittees(committee).length, 0) > 1;
    },
    inheritSourceOptions() {
      const options = [];

      (this.snapshot.coordinatingCommittees || []).forEach((committee) => {
        (committee.technicalCommittees || []).forEach((technicalCommittee) => {
          if (!technicalCommittee.id) {
            return;
          }

          const ccNumber = technicalCommittee.ccNumber ?? committee.number;
          const removed = Boolean(technicalCommittee.deleted || committee.deleted);
          const name = technicalCommittee.name ? ` ${technicalCommittee.name}` : '';
          const suffix = removed ? ' (removed)' : '';

          options.push({
            value: Number(technicalCommittee.id),
            text: `TC${ccNumber}.${technicalCommittee.number}${name}${suffix}`,
          });
        });
      });

      return options.sort((a, b) => a.text.localeCompare(b.text));
    },
    tcMoveOptionsByCcKey() {
      const committees = this.activeCommittees.map((committee, index) => ({
        committee,
        key: this.committeeKey(committee, index),
      }));

      const sortedTargets = [...committees]
        .sort((a, b) => Number(a.committee.number) - Number(b.committee.number));

      return Object.fromEntries(committees.map(({ key: sourceKey }) => [
        sourceKey,
        [
          { value: null, text: 'Move to CC…', disabled: true },
          ...sortedTargets
            .filter(({ key }) => key !== sourceKey)
            .map(({ committee, key }) => ({
              value: key,
              text: `CC${committee.number}${committee.name ? ` — ${committee.name}` : ''}`,
            })),
        ],
      ]));
    },
    keywordMoveOptionsByTcKey() {
      const allTargets = this.activeCommittees
        .flatMap((committee, ccIndex) => this.activeTechnicalCommittees(committee)
          .map((technicalCommittee, tcIndex) => {
            const tcKey = this.technicalCommitteeKey(technicalCommittee, tcIndex);

            return {
              committee,
              technicalCommittee,
              tcKey,
              encodedKey: this.encodeKeywordMoveTarget(
                this.committeeKey(committee, ccIndex),
                tcKey,
              ),
              label: `TC${committee.number}.${technicalCommittee.number}${technicalCommittee.name ? ` — ${technicalCommittee.name}` : ''}`,
            };
          }));

      const sortedTargets = [...allTargets].sort((a, b) => {
        const ccDiff = Number(a.committee.number) - Number(b.committee.number);
        if (ccDiff !== 0) {
          return ccDiff;
        }

        return Number(a.technicalCommittee.number) - Number(b.technicalCommittee.number);
      });

      return Object.fromEntries(allTargets.map(({ tcKey, encodedKey: sourceKey }) => [
        tcKey,
        [
          { value: null, text: 'Move to TC…', disabled: true },
          ...sortedTargets
            .filter(({ encodedKey }) => encodedKey !== sourceKey)
            .map(({ encodedKey, label }) => ({
              value: encodedKey,
              text: label,
            })),
        ],
      ]));
    },
    previewCommittees() {
      return this.activeCommittees
        .map((committee) => ({
          ...committee,
          urls: committee.urls || [],
          technicalCommittees: this.activeTechnicalCommittees(committee)
            .map((tc) => ({
              ...tc,
              ccNumber: committee.number,
              fullNumber: `${committee.number}.${tc.number}`,
              urls: tc.urls || [],
              keywords: this.activeKeywords(tc),
              conferencesMainSponsor: tc.conferencesMainSponsor || [],
              conferencesCoSponsor: tc.conferencesCoSponsor || [],
            }))
            .sort((a, b) => Number(a.number) - Number(b.number)),
        }))
        .sort((a, b) => Number(a.number) - Number(b.number));
    },
    pdfLink() {
      if (!this.revision) {
        return '#';
      }

      const token = this.$store.state.auth?.tokens?.token;

      return Revisions.pdfUrl(this.revision.id, token);
    },
    targetsNextTriennium() {
      if (!this.revision?.targetTrienniumNumber) {
        return false;
      }

      const current = this.$store.getters['settings/currentTriennium']
        ?? Triennium.calculateNumberFromYear();

      return this.revision.targetTrienniumNumber > current;
    },
  },
  async created() {
    await this.loadRevision();
  },
  methods: {
    activeTechnicalCommittees(committee) {
      return (committee.technicalCommittees || []).filter((tc) => !tc.deleted);
    },
    activeKeywords(technicalCommittee) {
      return (technicalCommittee.keywords || []).filter((keyword) => !keyword.deleted);
    },
    committeeKey(committee, index) {
      return String(committee.id || committee.draftId || `cc-${index}`);
    },
    technicalCommitteeKey(technicalCommittee, index) {
      return String(technicalCommittee.id || technicalCommittee.draftId || `tc-${index}`);
    },
    keywordKey(keyword, keywordIndex, technicalCommittee, tcIndex) {
      const parent = this.technicalCommitteeKey(technicalCommittee, tcIndex);

      return `${parent}::${String(keyword.id || keyword.draftId || `kw-${keywordIndex}`)}`;
    },
    encodeKeywordMoveTarget(ccKey, tcKey) {
      return `${String(ccKey)}::${String(tcKey)}`;
    },
    decodeKeywordMoveTarget(value) {
      if (!value || typeof value !== 'string') {
        return null;
      }

      const [ccKey, tcKey] = value.split('::');

      if (!ccKey || !tcKey) {
        return null;
      }

      return { ccKey, tcKey };
    },
    syncTechnicalCommitteeFullNumber(committee, technicalCommittee) {
      this.$set(technicalCommittee, 'ccNumber', committee.number);

      if (technicalCommittee.number != null && technicalCommittee.number !== '') {
        this.$set(
          technicalCommittee,
          'fullNumber',
          `${committee.number}.${technicalCommittee.number}`,
        );
      }
    },
    syncTechnicalCommitteeNumbers(committee) {
      (committee.technicalCommittees || []).forEach((technicalCommittee) => {
        if (technicalCommittee.deleted) {
          return;
        }

        this.syncTechnicalCommitteeFullNumber(committee, technicalCommittee);
      });
    },
    normalizeSnapshotNumbers() {
      (this.snapshot.coordinatingCommittees || []).forEach((committee) => {
        this.syncTechnicalCommitteeNumbers(committee);
      });
    },
    async loadRevision() {
      this.busy = true;
      this.forbidden = false;
      this.canStartReview = false;

      try {
        const permissionsResponse = await Revisions.permissions();
        const { canEditStructure } = permissionsResponse.data.data;

        if (!canEditStructure) {
          this.forbidden = true;
          return;
        }

        const response = await Revisions.activeStructure();
        this.revision = response.data.data;

        if (this.revision) {
          this.snapshot = JSON.parse(
            JSON.stringify(this.revision.snapshot || { coordinatingCommittees: [] }),
          );
          await this.refreshImpactWarnings();
        } else {
          this.canStartReview = true;
        }
      } catch (error) {
        if (error.response?.status === 403) {
          this.forbidden = true;
        } else {
          throw error;
        }
      } finally {
        this.busy = false;
      }
    },
    async startStructureReview() {
      this.busy = true;

      try {
        const response = await Revisions.create({
          type: 'structure',
          targetTriennium: this.targetTriennium,
        });
        this.revision = response.data.data;
        this.canStartReview = false;
        this.snapshot = JSON.parse(
          JSON.stringify(this.revision.snapshot || { coordinatingCommittees: [] }),
        );
        await this.refreshImpactWarnings();
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not start structure review.',
        });
      } finally {
        this.busy = false;
      }
    },
    async refreshImpactWarnings() {
      await Promise.all([
        this.refreshKeywordWarnings(),
        this.refreshSponsorshipWarnings(),
      ]);
    },
    async refreshKeywordWarnings() {
      if (!this.revision) {
        return;
      }

      const response = await Revisions.keywordRemovalImpact(this.revision.id);
      this.keywordWarnings = response.data.data.removedKeywords || [];
    },
    async refreshSponsorshipWarnings() {
      if (!this.revision) {
        return;
      }

      const response = await Revisions.sponsorshipInheritanceImpact(this.revision.id);
      this.sponsorshipWarnings = response.data.data.conferences || [];
    },
    sponsorshipWarningText(warning) {
      const parts = [];

      if (warning.addedMainCount) {
        parts.push(`gains ${warning.addedMainCount} main sponsor${warning.addedMainCount === 1 ? '' : 's'}`);
      }

      if (warning.addedCoCount) {
        parts.push(`gains ${warning.addedCoCount} co-sponsor${warning.addedCoCount === 1 ? '' : 's'}`);
      }

      if (warning.removedDeletedCount) {
        parts.push(`removes ${warning.removedDeletedCount} deleted sponsor${warning.removedDeletedCount === 1 ? '' : 's'}`);
      }

      if (warning.losesLastMainSponsor) {
        parts.push('would lose its last main sponsor');
      }

      return `${warning.name}: ${parts.join('; ')}.`;
    },
    async saveSnapshot(options = {}) {
      const silent = Boolean(options.silent);

      if (!silent) {
        this.busy = true;
      }

      try {
        this.normalizeSnapshotNumbers();
        const response = await Revisions.update(this.revision.id, this.snapshot);
        this.revision = response.data.data;
        this.snapshot = JSON.parse(JSON.stringify(this.revision.snapshot));
        await this.refreshImpactWarnings();

        if (!silent) {
          this.$snack.success({ text: 'Structure saved.' });
        }
      } finally {
        if (!silent) {
          this.busy = false;
        }
      }
    },
    async discardRevision() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Discard all proposed changes and return to the live structure?',
      );

      if (!confirmed) {
        return;
      }

      this.busy = true;

      try {
        await Revisions.discard(this.revision.id);
        await this.loadRevision();
        this.$snack.success({ text: 'Revision discarded.' });
      } finally {
        this.busy = false;
      }
    },
    async requestValidation() {
      this.busy = true;

      try {
        await this.saveSnapshot({ silent: true });
        const response = await Revisions.requestValidation(this.revision.id);
        this.revision = response.data.data;
        this.$snack.success({ text: 'Validation requested.' });
        this.$router.push('/areas/validate');
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not request validation.',
        });
      } finally {
        this.busy = false;
      }
    },
    async applyRevision() {
      const message = this.targetsNextTriennium
        ? 'Apply this validated structure for the next triennium? It will be staged and the public Technical Areas page will not change until Admin switches the current triennium setting to that period.'
        : 'Apply this validated structure to the live technical areas?';

      const confirmed = await this.$bvModal.msgBoxConfirm(message);

      if (!confirmed) {
        return;
      }

      this.busy = true;

      try {
        await Revisions.apply(this.revision.id);
        this.$snack.success({ text: 'Structure applied.' });
        this.$router.push('/areas');
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not apply structure changes.',
        });
      } finally {
        this.busy = false;
      }
    },
    addCoordinatingCommittee() {
      this.snapshot.coordinatingCommittees.unshift({
        draftId: draftId(),
        number: this.nextCoordinatingCommitteeNumber(),
        name: '',
        description: '',
        technicalCommittees: [],
      });
    },
    removeCoordinatingCommittee(ccIndex) {
      const committee = this.activeCommittees[ccIndex];
      const actualIndex = this.snapshot.coordinatingCommittees.indexOf(committee);

      if (committee.id) {
        this.$set(this.snapshot.coordinatingCommittees[actualIndex], 'deleted', true);
      } else {
        this.snapshot.coordinatingCommittees.splice(actualIndex, 1);
      }
    },
    addTechnicalCommittee(ccIndex) {
      const committee = this.activeCommittees[ccIndex];
      const actualIndex = this.snapshot.coordinatingCommittees.indexOf(committee);

      if (!committee.technicalCommittees) {
        this.$set(committee, 'technicalCommittees', []);
      }

      const number = this.nextTechnicalCommitteeNumber(committee);
      committee.technicalCommittees.push({
        draftId: draftId(),
        number,
        ccNumber: committee.number,
        fullNumber: `${committee.number}.${number}`,
        name: '',
        description: '',
        keywords: [],
        inheritMainSponsorshipFromTcIds: [],
        inheritCoSponsorshipFromTcIds: [],
      });

      this.$set(this.snapshot.coordinatingCommittees, actualIndex, committee);
    },
    removeTechnicalCommittee(ccIndex, tcIndex) {
      const committee = this.activeCommittees[ccIndex];
      const technicalCommittee = this.activeTechnicalCommittees(committee)[tcIndex];
      const actualIndex = committee.technicalCommittees.indexOf(technicalCommittee);

      if (technicalCommittee.id) {
        this.$set(committee.technicalCommittees[actualIndex], 'deleted', true);
      } else {
        committee.technicalCommittees.splice(actualIndex, 1);
      }
    },
    setMoveTarget(tcKey, targetCcKey) {
      this.$set(this.moveTargets, tcKey, targetCcKey);
    },
    nextCoordinatingCommitteeNumber() {
      const numbers = this.activeCommittees
        .map((committee) => Number(committee.number))
        .filter((number) => !Number.isNaN(number));

      if (!numbers.length) {
        return 1;
      }

      return Math.max(...numbers) + 1;
    },
    nextTechnicalCommitteeNumber(committee) {
      const numbers = this.activeTechnicalCommittees(committee)
        .map((tc) => Number(tc.number))
        .filter((number) => !Number.isNaN(number));

      if (!numbers.length) {
        return 1;
      }

      return Math.max(...numbers) + 1;
    },
    moveTechnicalCommittee(sourceCcIndex, tcIndex, targetCcKey) {
      if (!targetCcKey) {
        return;
      }

      const sourceCommittee = this.activeCommittees[sourceCcIndex];
      const targetCommittee = this.activeCommittees.find(
        (committee, index) => this.committeeKey(committee, index) === targetCcKey,
      );

      if (!sourceCommittee || !targetCommittee || sourceCommittee === targetCommittee) {
        return;
      }

      const technicalCommittee = this.activeTechnicalCommittees(sourceCommittee)[tcIndex];

      if (!technicalCommittee) {
        return;
      }

      const sourceTcIndex = sourceCommittee.technicalCommittees.indexOf(technicalCommittee);
      const tcKey = this.technicalCommitteeKey(technicalCommittee, tcIndex);

      sourceCommittee.technicalCommittees.splice(sourceTcIndex, 1);

      if (!targetCommittee.technicalCommittees) {
        this.$set(targetCommittee, 'technicalCommittees', []);
      }

      const nextNumber = this.nextTechnicalCommitteeNumber(targetCommittee);
      technicalCommittee.number = nextNumber;
      this.$set(technicalCommittee, 'ccNumber', targetCommittee.number);
      this.$set(technicalCommittee, 'fullNumber', `${targetCommittee.number}.${nextNumber}`);

      targetCommittee.technicalCommittees.push(technicalCommittee);
      this.$delete(this.moveTargets, tcKey);
    },
    addKeyword(ccIndex, tcIndex) {
      const committee = this.activeCommittees[ccIndex];
      const technicalCommittee = this.activeTechnicalCommittees(committee)[tcIndex];

      if (!technicalCommittee.keywords) {
        this.$set(technicalCommittee, 'keywords', []);
      }

      technicalCommittee.keywords.push({
        draftId: draftId(),
        name: '',
      });
    },
    removeKeyword(ccIndex, tcIndex, keywordIndex) {
      const committee = this.activeCommittees[ccIndex];
      const technicalCommittee = this.activeTechnicalCommittees(committee)[tcIndex];
      const keyword = this.activeKeywords(technicalCommittee)[keywordIndex];
      const actualIndex = technicalCommittee.keywords.indexOf(keyword);

      if (keyword.id) {
        this.$set(technicalCommittee.keywords[actualIndex], 'deleted', true);
      } else {
        technicalCommittee.keywords.splice(actualIndex, 1);
      }
    },
    setKeywordMoveTarget(keywordKey, targetTcKey) {
      this.$set(this.keywordMoveTargets, keywordKey, targetTcKey);
    },
    moveKeyword(sourceCcIndex, sourceTcIndex, keywordIndex, targetTcKey) {
      const decoded = this.decodeKeywordMoveTarget(targetTcKey);

      if (!decoded) {
        return;
      }

      const { ccKey: targetCcKey, tcKey: targetOnlyTcKey } = decoded;
      const sourceCommittee = this.activeCommittees[sourceCcIndex];
      const sourceTc = this.activeTechnicalCommittees(sourceCommittee)[sourceTcIndex];
      const keyword = this.activeKeywords(sourceTc)[keywordIndex];

      if (!keyword || !sourceTc) {
        return;
      }

      const targetCommittee = this.activeCommittees.find(
        (committee, index) => this.committeeKey(committee, index) === targetCcKey,
      );

      if (!targetCommittee) {
        return;
      }

      const targetTc = this.activeTechnicalCommittees(targetCommittee).find(
        (technicalCommittee, index) => this.technicalCommitteeKey(technicalCommittee, index) === targetOnlyTcKey,
      );

      if (!targetTc || targetTc === sourceTc) {
        return;
      }

      const sourceKeywordIndex = sourceTc.keywords.indexOf(keyword);
      const moveKey = this.keywordKey(keyword, keywordIndex, sourceTc, sourceTcIndex);

      sourceTc.keywords.splice(sourceKeywordIndex, 1);

      if (!targetTc.keywords) {
        this.$set(targetTc, 'keywords', []);
      }

      targetTc.keywords.push(keyword);
      this.$delete(this.keywordMoveTargets, moveKey);
      this.refreshImpactWarnings();
    },
    async checkKeywordSimilarity(keyword) {
      if (!keyword.name || !this.revision?.id) {
        return;
      }

      const excludeKeywordIds = keyword.id ? [keyword.id] : [];
      const response = await Revisions.checkKeywordSimilarity(keyword.name, {
        revisionId: this.revision.id,
        excludeKeywordIds,
        excludeDraftId: keyword.draftId,
      });
      const snack = Revisions.keywordSimilaritySnack(response.data.data);

      if (!snack) {
        return;
      }

      if (snack.variant === 'danger') {
        this.$snack.danger({ text: snack.text });
      } else {
        this.$snack.show({ text: snack.text });
      }
    },
  },
};
</script>
